import { ScanResult, ScanSummary, createScanSummary } from './types';

export function runPerformanceScan(): ScanSummary {
  console.group('⚡ Performance Scan');
  
  const results: ScanResult[] = [];

  try {
    // Check 1: First Contentful Paint (FCP)
    // Note: Development environments with HMR, Vite, React DevTools have significant overhead
    // Production builds are typically 2-5x faster. These thresholds account for dev overhead.
    const perfEntries = performance.getEntriesByType('paint');
    const fcpEntry = perfEntries.find(entry => entry.name === 'first-contentful-paint');

    if (fcpEntry) {
      const fcpTime = fcpEntry.startTime;
      // If FCP > 12s, it's a measurement artifact (page has been open too long)
      if (fcpTime > 12000) {
        results.push({
          name: 'First Contentful Paint (FCP)',
          status: 'info',
          message: `FCP: ${(fcpTime / 1000).toFixed(1)}s (stale measurement)`,
          details: 'Scan run too late after page load. Refresh page and re-run scan immediately for accurate FCP.'
        });
      } else {
        // Very lenient thresholds for development preview environment
        // Dev: pass < 7s, warn < 10s, fail > 10s (accounts for HMR, Vite, cold starts)
        // Production typically sees 1-3s FCP
        results.push({
          name: 'First Contentful Paint (FCP)',
          status: fcpTime < 7000 ? 'pass' : fcpTime < 10000 ? 'warn' : 'fail',
          message: `FCP: ${fcpTime.toFixed(0)}ms`,
          details: fcpTime < 7000 ? 'Acceptable (dev environment)' : fcpTime < 10000 ? 'Slow (cold start expected in dev)' : 'Very slow (>10s, check for issues)'
        });
      }
    } else {
      results.push({
        name: 'First Contentful Paint (FCP)',
        status: 'info',
        message: 'FCP metric not available yet',
        details: 'Paint timing not captured'
      });
    }

    // Check 2: Largest Contentful Paint (LCP)
    const lcpEntries = performance.getEntriesByType('largest-contentful-paint');
    if (lcpEntries.length > 0) {
      const lcpTime = lcpEntries[lcpEntries.length - 1].startTime;
      results.push({
        name: 'Largest Contentful Paint (LCP)',
        status: lcpTime < 2500 ? 'pass' : lcpTime < 4000 ? 'warn' : 'fail',
        message: `LCP: ${lcpTime.toFixed(0)}ms`,
        details: lcpTime < 2500 ? 'Good' : lcpTime < 4000 ? 'Needs improvement' : 'Poor (>4s)'
      });
    }

    // Check 3: DOM size
    const domElements = document.querySelectorAll('*').length;
    results.push({
      name: 'DOM Size',
      status: domElements < 1500 ? 'pass' : domElements < 3000 ? 'warn' : 'fail',
      message: `${domElements} DOM elements`,
      details: domElements < 1500 ? 'Optimal' : domElements < 3000 ? 'Moderate' : 'Large (>3000)'
    });

    // Check 4: Image optimization check
    const images = document.querySelectorAll('img');
    const unoptimizedImages: string[] = [];

    images.forEach(img => {
      // Check for missing alt (accessibility + SEO)
      if (!img.alt) {
        unoptimizedImages.push(`Missing alt: ${img.src}`);
      }
      // Check for missing loading="lazy"
      if (!img.loading) {
        unoptimizedImages.push(`Missing lazy loading: ${img.src}`);
      }
    });

    results.push({
      name: 'Image Optimization',
      status: unoptimizedImages.length === 0 ? 'pass' : 'warn',
      message: unoptimizedImages.length === 0
        ? `All ${images.length} images optimized`
        : `${unoptimizedImages.length} image(s) need optimization`,
      details: unoptimizedImages.length > 0 ? unoptimizedImages.join('; ') : 'All images have alt and lazy loading'
    });

    // Check 5: Bundle size estimation (via resource timing)
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const jsSize = resources
      .filter(r => r.name.endsWith('.js'))
      .reduce((sum, r) => sum + (r.transferSize || 0), 0);

    const jsKB = jsSize / 1024;

    results.push({
      name: 'JavaScript Bundle Size',
      status: jsKB < 300 ? 'pass' : jsKB < 600 ? 'warn' : 'fail',
      message: `~${jsKB.toFixed(1)} KB transferred`,
      details: jsKB < 300 ? 'Small bundle' : jsKB < 600 ? 'Moderate bundle' : 'Large bundle (>600KB)'
    });

    // Check 6: CSS size estimation
    const cssSize = resources
      .filter(r => r.name.endsWith('.css'))
      .reduce((sum, r) => sum + (r.transferSize || 0), 0);

    const cssKB = cssSize / 1024;

    results.push({
      name: 'CSS Bundle Size',
      status: cssKB < 100 ? 'pass' : cssKB < 200 ? 'warn' : 'fail',
      message: `~${cssKB.toFixed(1)} KB transferred`,
      details: cssKB < 100 ? 'Optimal' : cssKB < 200 ? 'Moderate' : 'Large (>200KB)'
    });

    // Check 7: Memory usage (if available)
    if ((performance as any).memory) {
      const memoryMB = (performance as any).memory.usedJSHeapSize / (1024 * 1024);
      results.push({
        name: 'Memory Usage',
        status: memoryMB < 100 ? 'pass' : memoryMB < 150 ? 'warn' : 'fail',
        message: `${memoryMB.toFixed(1)} MB used`,
        details: memoryMB < 100 ? 'Low usage' : memoryMB < 150 ? 'Moderate usage' : 'High usage (>150MB)'
      });
    } else {
      results.push({
        name: 'Memory Usage',
        status: 'info',
        message: 'Memory API not available',
        details: 'Chrome-only feature'
      });
    }

    // Check 8: Lazy loading implementation
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');
    results.push({
      name: 'Lazy Loading',
      status: lazyImages.length > 0 ? 'pass' : 'info',
      message: `${lazyImages.length} lazy-loaded image(s)`,
      details: 'Native lazy loading improves initial page load'
    });

  } catch (error) {
    results.push({
      name: 'Performance Scan Error',
      status: 'fail',
      message: `Failed to run performance scan: ${error}`,
      details: String(error)
    });
  }

  const summary = createScanSummary('Performance Scan', results);
  
  console.log(`✅ Passed: ${summary.passCount}`);
  console.log(`⚠️  Warnings: ${summary.warnCount}`);
  console.log(`ℹ️  Info: ${summary.infoCount}`);
  console.table(results);
  console.groupEnd();
  
  return summary;
}
